import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dumbbell, Sparkles, Plus, Clock, Flame, Activity, Target,
  Brain, ChevronDown, Loader2, Trash2, Zap, Heart, Shield
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKOUT_TYPES = [
  { value: 'strength', label: 'Strength', icon: Dumbbell },
  { value: 'cardio', label: 'Cardio', icon: Activity },
  { value: 'flexibility', label: 'Flexibility', icon: Heart },
  { value: 'sport-specific', label: 'Sport-Specific', icon: Target },
  { value: 'hiit', label: 'HIIT', icon: Zap },
  { value: 'recovery', label: 'Recovery', icon: Shield },
];

const MENTAL_FOCUS_OPTIONS = [
  'confidence', 'focus', 'stress-management', 'resilience',
  'motivation', 'emotional-awareness', 'goal-setting', 'self-talk'
];

const PHYSICAL_GOAL_OPTIONS = [
  'endurance', 'strength', 'flexibility', 'speed', 'power',
  'balance', 'coordination', 'injury-prevention'
];

export default function PhysicalWorkouts({ user, onLogout }) {
  const [tab, setTab] = useState("log");
  const [workouts, setWorkouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const navigate = useNavigate();

  // Log form state
  const [logForm, setLogForm] = useState({
    workout_type: 'strength', title: '', duration_minutes: 30,
    intensity: 'moderate', sport: '', notes: '', exercises: []
  });
  const [exerciseInput, setExerciseInput] = useState('');

  // Recommend form state
  const [recForm, setRecForm] = useState({
    sport: '', mental_focus_areas: [], physical_goals: [],
    fitness_level: 'intermediate', duration_minutes: 45
  });

  const [aiPlans, setAiPlans] = useState([]);
  const [generatingAiPlan, setGeneratingAiPlan] = useState(false);
  const [aiPlanForm, setAiPlanForm] = useState({ focus: '', duration_weeks: 1, sessions_per_week: 3 });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWorkouts = async () => {
    try {
      const res = await axios.get(`${API}/workouts`, { headers });
      setWorkouts(res.data);
      // Separate AI Coach plans
      setAiPlans(res.data.filter(w => w.source === 'ai_coach'));
    } catch (err) { console.error("Fetch error:", err); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/workouts/stats`, { headers });
      setStats(res.data);
    } catch (err) { console.error("Fetch error:", err); }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/workouts/recommendations`, { headers });
      setRecommendations(res.data);
    } catch (err) { console.error("Fetch error:", err); }
  };

  useEffect(() => {
    fetchWorkouts();
    fetchStats();
    fetchRecommendations();
  }, []);

  const handleLogWorkout = async (e) => {
    e.preventDefault();
    if (!logForm.title.trim()) { toast.error("Please enter a workout title"); return; }
    try {
      await axios.post(`${API}/workouts`, logForm, { headers });
      toast.success("Workout logged!");
      setShowLogForm(false);
      setLogForm({ workout_type: 'strength', title: '', duration_minutes: 30, intensity: 'moderate', sport: '', notes: '', exercises: [] });
      setExerciseInput('');
      fetchWorkouts();
      fetchStats();
    } catch { toast.error("Failed to log workout"); }
  };

  const handleDeleteWorkout = async (id) => {
    try {
      await axios.delete(`${API}/workouts/${id}`, { headers });
      toast.success("Workout deleted");
      fetchWorkouts();
      fetchStats();
    } catch { toast.error("Failed to delete"); }
  };

  const handleGenerate = async () => {
    if (!recForm.sport.trim()) { toast.error("Please enter your sport"); return; }
    if (recForm.mental_focus_areas.length === 0) { toast.error("Select at least one mental focus area"); return; }
    if (recForm.physical_goals.length === 0) { toast.error("Select at least one physical goal"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/workouts/recommend`, recForm, { headers });
      setRecommendations(prev => [res.data, ...prev]);
      toast.success("Workout plan generated!");
      setTab("plans");
    } catch (err) {
      toast.error("Failed to generate workout plan");
    } finally { setGenerating(false); }
  };

  const toggleArrayItem = (arr, item) => arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

  const handleGenerateAiPlan = async () => {
    if (!aiPlanForm.focus.trim()) { toast.error("Please describe your focus"); return; }
    setGeneratingAiPlan(true);
    try {
      const res = await axios.post(`${API}/ai/generate-workout-plan`, aiPlanForm, { headers });
      toast.success(`Generated ${res.data.count} workout sessions!`);
      fetchWorkouts();
    } catch { toast.error("Failed to generate plan"); }
    finally { setGeneratingAiPlan(false); }
  };

  const addExercise = () => {
    if (exerciseInput.trim()) {
      setLogForm(prev => ({ ...prev, exercises: [...prev.exercises, exerciseInput.trim()] }));
      setExerciseInput('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 data-testid="workouts-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
              Physical Workouts
            </h1>
            <p className="text-sm text-gray-500 mt-1">Log workouts & get AI-powered recommendations</p>
          </div>
          <Button data-testid="log-workout-btn" onClick={() => setShowLogForm(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            <Plus className="w-4 h-4" /> Log Workout
          </Button>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="workout-stats">
            {[
              { label: 'Total Workouts', value: stats.total_workouts, icon: Dumbbell, color: 'text-emerald-600 bg-emerald-50' },
              { label: 'Total Minutes', value: stats.total_minutes, icon: Clock, color: 'text-blue-600 bg-blue-50' },
              { label: 'This Week', value: stats.weekly_workouts, icon: Flame, color: 'text-orange-600 bg-orange-50' },
              { label: 'Weekly Minutes', value: stats.weekly_minutes, icon: Activity, color: 'text-violet-600 bg-violet-50' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
                  <s.icon className="w-4 h-4" />
                </div>
                <p className="text-2xl font-black text-gray-900">{s.value}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-3 gap-1">
            <TabsTrigger data-testid="tab-log" value="log" className="gap-1.5 text-xs"><Dumbbell className="w-3.5 h-3.5" /> History</TabsTrigger>
            <TabsTrigger data-testid="tab-recommend" value="recommend" className="gap-1.5 text-xs"><Sparkles className="w-3.5 h-3.5" /> Generate</TabsTrigger>
            <TabsTrigger data-testid="tab-plans" value="plans" className="gap-1.5 text-xs"><Brain className="w-3.5 h-3.5" /> Plans</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* ─── LOG FORM MODAL ─── */}
        {showLogForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Log Workout</h2>
              <form onSubmit={handleLogWorkout} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Workout Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {WORKOUT_TYPES.map(t => (
                      <button key={t.value} type="button"
                        onClick={() => setLogForm(prev => ({ ...prev, workout_type: t.value }))}
                        className={`p-2.5 rounded-xl text-xs font-semibold border-2 transition-all flex flex-col items-center gap-1 ${
                          logForm.workout_type === t.value
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}>
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Workout Title</label>
                  <input data-testid="workout-title-input" value={logForm.title} onChange={e => setLogForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none"
                    placeholder="e.g., Upper Body Strength" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Duration (min)</label>
                    <input type="number" value={logForm.duration_minutes} onChange={e => setLogForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Intensity</label>
                    <select value={logForm.intensity} onChange={e => setLogForm(prev => ({ ...prev, intensity: e.target.value }))}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none">
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sport (optional)</label>
                  <input value={logForm.sport} onChange={e => setLogForm(prev => ({ ...prev, sport: e.target.value }))}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none"
                    placeholder="e.g., Basketball, Soccer" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Exercises</label>
                  <div className="flex gap-2">
                    <input value={exerciseInput} onChange={e => setExerciseInput(e.target.value)} onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); addExercise(); } }}
                      className="flex-1 px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none"
                      placeholder="Add an exercise and press Enter" />
                    <Button type="button" onClick={addExercise} size="sm" variant="outline">Add</Button>
                  </div>
                  {logForm.exercises.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {logForm.exercises.map((ex, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full font-medium">
                          {ex}
                          <button type="button" onClick={() => setLogForm(prev => ({ ...prev, exercises: prev.exercises.filter((_, j) => j !== i) }))} className="text-emerald-400 hover:text-emerald-600">
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes (optional)</label>
                  <textarea value={logForm.notes} onChange={e => setLogForm(prev => ({ ...prev, notes: e.target.value }))} rows={2}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none resize-none"
                    placeholder="How did it feel?" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button data-testid="submit-workout-btn" type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">Save Workout</Button>
                  <Button type="button" variant="outline" onClick={() => setShowLogForm(false)}>Cancel</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── HISTORY TAB ─── */}
        {tab === "log" && (
          <div className="space-y-3" data-testid="workout-history">
            {workouts.length === 0 ? (
              <div className="text-center py-16">
                <Dumbbell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">No workouts logged yet</p>
                <p className="text-gray-300 text-sm mt-1">Click &ldquo;Log Workout&rdquo; to get started</p>
              </div>
            ) : (
              workouts.map(w => {
                const TypeIcon = WORKOUT_TYPES.find(t => t.value === w.workout_type)?.icon || Dumbbell;
                return (
                  <div key={w.id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600">
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{w.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.duration_minutes} min</span>
                            <span className="capitalize px-2 py-0.5 bg-gray-100 rounded-full">{w.intensity}</span>
                            <span className="capitalize px-2 py-0.5 bg-gray-100 rounded-full">{w.workout_type}</span>
                            {w.sport && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{w.sport}</span>}
                          </div>
                          {w.exercises?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {w.exercises.map((ex, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">{ex}</span>
                              ))}
                            </div>
                          )}
                          {w.notes && <p className="text-xs text-gray-400 mt-1.5">{w.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{new Date(w.created_at).toLocaleDateString()}</span>
                        <button onClick={() => handleDeleteWorkout(w.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ─── GENERATE TAB ─── */}
        {tab === "recommend" && (
          <div className="max-w-2xl" data-testid="workout-generate">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  AI Workout Generator
                </CardTitle>
                <CardDescription>Get a personalized workout that connects physical training to mental performance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Sport</label>
                  <input data-testid="rec-sport-input" value={recForm.sport} onChange={e => setRecForm(prev => ({ ...prev, sport: e.target.value }))}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none"
                    placeholder="e.g., Basketball, Tennis, Soccer" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Mental Focus Areas</label>
                  <div className="flex flex-wrap gap-2">
                    {MENTAL_FOCUS_OPTIONS.map(opt => (
                      <button key={opt} type="button" data-testid={`mental-${opt}`}
                        onClick={() => setRecForm(prev => ({ ...prev, mental_focus_areas: toggleArrayItem(prev.mental_focus_areas, opt) }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all capitalize ${
                          recForm.mental_focus_areas.includes(opt)
                            ? 'bg-violet-50 border-violet-500 text-violet-700'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}>
                        {opt.replace(/-/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Physical Goals</label>
                  <div className="flex flex-wrap gap-2">
                    {PHYSICAL_GOAL_OPTIONS.map(opt => (
                      <button key={opt} type="button" data-testid={`goal-${opt}`}
                        onClick={() => setRecForm(prev => ({ ...prev, physical_goals: toggleArrayItem(prev.physical_goals, opt) }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all capitalize ${
                          recForm.physical_goals.includes(opt)
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}>
                        {opt.replace(/-/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fitness Level</label>
                    <select value={recForm.fitness_level} onChange={e => setRecForm(prev => ({ ...prev, fitness_level: e.target.value }))}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none">
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Duration (min)</label>
                    <input type="number" value={recForm.duration_minutes} onChange={e => setRecForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 outline-none" />
                  </div>
                </div>

                <Button data-testid="generate-workout-btn" onClick={handleGenerate} disabled={generating}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 h-12 text-sm font-semibold gap-2">
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Workout Plan</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── PLANS TAB ─── */}
        {tab === "plans" && (
          <div className="space-y-6" data-testid="workout-plans">
            {/* AI Coach Plan Generator */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-indigo-50">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-violet-600" />
                  AI Coach Workout Plan
                </h3>
                <p className="text-xs text-gray-500 mb-3">Generate a structured workout plan tailored to your sport and mental focus</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <input data-testid="ai-plan-focus" type="text" value={aiPlanForm.focus} onChange={e => setAiPlanForm(p => ({...p, focus: e.target.value}))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Focus: e.g. pre-game preparation" />
                  <select value={aiPlanForm.duration_weeks} onChange={e => setAiPlanForm(p => ({...p, duration_weeks: parseInt(e.target.value)}))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value={1}>1 Week</option><option value={2}>2 Weeks</option><option value={4}>4 Weeks</option>
                  </select>
                  <select value={aiPlanForm.sessions_per_week} onChange={e => setAiPlanForm(p => ({...p, sessions_per_week: parseInt(e.target.value)}))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value={3}>3x/week</option><option value={4}>4x/week</option><option value={5}>5x/week</option><option value={6}>6x/week</option>
                  </select>
                </div>
                <Button data-testid="generate-ai-plan-btn" onClick={handleGenerateAiPlan} disabled={generatingAiPlan}
                  className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold gap-1.5">
                  {generatingAiPlan ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : <><Brain className="w-3.5 h-3.5" /> Generate AI Plan</>}
                </Button>
              </CardContent>
            </Card>

            {/* AI Coach Plan Cards */}
            {aiPlans.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3">AI Coach Workout Plans</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {aiPlans.map(plan => (
                    <Card key={plan.id} data-testid={`ai-plan-card-${plan.id}`} className="border-0 shadow-sm hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className={`p-1.5 rounded-lg ${
                            plan.workout_type === 'strength' ? 'bg-emerald-100' :
                            plan.workout_type === 'cardio' ? 'bg-blue-100' :
                            plan.workout_type === 'flexibility' ? 'bg-pink-100' :
                            plan.workout_type === 'recovery' ? 'bg-amber-100' : 'bg-gray-100'
                          }`}>
                            <Dumbbell className="w-4 h-4 text-gray-700" />
                          </div>
                          <div className="flex items-center gap-1.5">
                            {plan.week && <span className="text-[9px] px-1.5 py-0.5 bg-violet-50 text-violet-700 rounded-full font-semibold">Wk {plan.week}</span>}
                            {plan.day && <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-semibold">{plan.day}</span>}
                          </div>
                        </div>
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{plan.title}</h4>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-2">
                          <span className="capitalize">{plan.workout_type}</span>
                          <span>&middot;</span>
                          <span>{plan.duration_minutes}min</span>
                          <span>&middot;</span>
                          <span className="capitalize">{plan.intensity}</span>
                        </div>
                        {plan.exercises?.length > 0 && (
                          <ul className="text-xs text-gray-600 space-y-0.5 mb-2">
                            {plan.exercises.slice(0, 4).map((ex, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-emerald-500 mt-0.5">-</span> {ex}
                              </li>
                            ))}
                            {plan.exercises.length > 4 && <li className="text-gray-400">+{plan.exercises.length - 4} more</li>}
                          </ul>
                        )}
                        {plan.mental_connection && (
                          <p className="text-[10px] text-violet-600 italic bg-violet-50 rounded-lg p-2">{plan.mental_connection}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Recommendation Plans */}
            {recommendations.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3">Generated Recommendation Plans</h3>
                {recommendations.map(rec => (
                <Card key={rec.id} className="border-0 shadow-sm hover:shadow-md transition-all">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{rec.sport} Workout</CardTitle>
                        <CardDescription className="text-xs">
                          {rec.fitness_level} &middot; {rec.duration_minutes} min &middot; {new Date(rec.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1.5 flex-wrap justify-end">
                        {rec.mental_focus_areas?.map(a => (
                          <span key={a} className="text-[10px] px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full capitalize">{a.replace(/-/g, ' ')}</span>
                        ))}
                        {rec.physical_goals?.map(g => (
                          <span key={g} className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full capitalize">{g.replace(/-/g, ' ')}</span>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_strong]:text-gray-900 [&_li]:text-sm">
                      {rec.recommendation?.split('\n').map((line, i) => {
                        if (line.startsWith('### ')) return <h3 key={i} className="font-bold text-gray-900 mt-3 mb-1">{line.replace('### ', '')}</h3>;
                        if (line.startsWith('## ')) return <h2 key={i} className="font-bold text-gray-900 mt-4 mb-2">{line.replace('## ', '')}</h2>;
                        if (line.startsWith('# ')) return <h1 key={i} className="font-bold text-gray-900 mt-4 mb-2">{line.replace('# ', '')}</h1>;
                        if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4">{line.replace(/^[-*]\s/, '')}</li>;
                        if (line.trim() === '') return <br key={i} />;
                        // Bold handling
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        return <p key={i}>{parts.map((part, j) =>
                          part.startsWith('**') && part.endsWith('**')
                            ? <strong key={j}>{part.slice(2, -2)}</strong>
                            : part
                        )}</p>;
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}

            {recommendations.length === 0 && aiPlans.length === 0 && (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">No workout plans yet</p>
                <p className="text-gray-300 text-sm mt-1">Use the AI Coach generator above or go to &ldquo;Generate&rdquo; tab</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
